import Dropdown from 'components/Form/Dropdown';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import KeyValue from 'components/Layout/KeyValue';
import SideBySide from 'components/Layout/SideBySide';
import Link from 'components/Link';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import Value from 'components/Value';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiErrorAxios} from 'utils/request-axios/utils';

import {dataTypesToOptions} from '../helpers';
import {useDeleteExtendedDataField, useEditExtendedDataField} from './services';
import {BackendData, FormValues} from './types';

const Edit: FC = () => {
    const {data} = useBackend<BackendData>();
    const methods = useForm<FormValues>({
        defaultValues: data,
    });

    const editApi = useEditExtendedDataField(data.mid);
    const deleteApi = useDeleteExtendedDataField(data.mid);
    const apiRequestPending = editApi.isLoading || deleteApi.isLoading;

    const handleSubmit = (values: FormValues) => {
        editApi
            .mutateAsync({
                label: values.label,
            })
            .then(() => {
                setUrl(window.Urls.extendeddatafields_list_view());
            })
            .catch(onApiErrorAxios(methods));
    };

    const handleDelete = () => {
        deleteApi
            .mutateAsync({})
            .then(() => {
                setUrl(window.Urls.extendeddatafields_list_view());
            })
            .catch(onApiErrorAxios(methods));
    };

    return (
        <div>
            <NavigationTabs.DocumentSettings selection="extendedDataFields" />
            <SectionHeader
                button={{
                    disabled:
                        !data.permissions.canDelete.allowed ||
                        apiRequestPending,
                    kind: 'destructive',
                    label: t`Delete`,
                    onClick: handleDelete,
                }}
                title={
                    data.objectType === 'document'
                        ? t`Edit Document Extended Data Field`
                        : t`Edit Line Item Extended Data Field`
                }
                tooltipMessage={data.permissions.canDelete.failureReason}
            />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <SideBySide className="mt-4 max-w-lg">
                        <InputText
                            label={t`Display Name`}
                            name="label"
                            required={true}
                        />
                        <InputText
                            disabled={true}
                            label={t`Key`}
                            name="key"
                            value={data.key}
                        />
                        <Dropdown
                            disabled={true}
                            label={t`Data Type`}
                            name="dataType"
                            options={dataTypesToOptions(data.dataTypes)}
                            type="select"
                            value={data.dataType}
                        />
                        <FormActions>
                            <SubmitButton
                                disabled={
                                    !methods.formState.isValid ||
                                    apiRequestPending
                                }
                            >{t`Save`}</SubmitButton>
                        </FormActions>
                    </SideBySide>
                </form>
            </FormProvider>
            {data.customtemplates.length > 0 && (
                <KeyValue keyWidth={10}>
                    <Field label={t`Custom Templates Being Used`} type="value">
                        <Value
                            value={{
                                type: 'custom',
                                value: (
                                    <div className="flex flex-col gap-2">
                                        {data.customtemplates.map(
                                            (template) => (
                                                <Link
                                                    href={template.url}
                                                    isOpenInNewTab={true}
                                                    key={template.url}
                                                >
                                                    {template.displayName}
                                                </Link>
                                            ),
                                        )}
                                        ,
                                    </div>
                                ),
                            }}
                        />
                    </Field>
                </KeyValue>
            )}
        </div>
    );
};

export default Edit;
